import { DAY_PART_IDS, DAY_PART_SELECTIONS } from 'constants/menu';

import { IDayPartsOperatingHoursFragment } from 'generated/graphql-gateway';

import { IValidDayPart } from './types';

type WeekdayAbbreviation = 'sun' | 'mon' | 'tue' | 'wed' | 'thr' | 'fri' | 'sat';

/**
 * Returns the current day of the week as a three-letter abbreviation.
 *
 * @returns {WeekdayAbbreviation} today's weekday as an abbreviation
 */
export const todayWeekdayAbbreviation = (): WeekdayAbbreviation => {
  const today = new Date();
  const daysOfWeek = ['sun', 'mon', 'tue', 'wed', 'thr', 'fri', 'sat'] as const;
  const dayIndex = today.getDay();
  return daysOfWeek[dayIndex];
};

/**
 * Returns the day part selection based on the day part id.
 *
 * @param id - Day part id
 * @returns Day part selection key
 */
export function retrieveDayPartKeyFromId(id?: string | null): string {
  const idToSelectionMap: { [key: string]: string } = {
    [DAY_PART_IDS.ALL_KEY]: DAY_PART_SELECTIONS.ALL_KEY,
    [DAY_PART_IDS.BREAKFAST_KEY]: DAY_PART_SELECTIONS.BREAKFAST_KEY,
    [DAY_PART_IDS.BURGERS_FOR_BREAKFAST]: DAY_PART_SELECTIONS.BURGERS_FOR_BREAKFAST,
    [DAY_PART_IDS.MAIN_MENU_KEY]: DAY_PART_SELECTIONS.MAIN_MENU_KEY,
  };

  return id && idToSelectionMap.hasOwnProperty(id) ? idToSelectionMap[id] : '';
}

/**
 * Remaps the store's day parts to the IValidDayPart type.
 *
 * @param dayParts - Store day parts
 * @returns Day parts as an IValidDayParts array
 */
export const remapDayParts = (
  dayParts: IDayPartsOperatingHoursFragment[] | undefined | null
): IValidDayPart[] => {
  const today = todayWeekdayAbbreviation();

  const remappedDayParts =
    dayParts?.map(dayPart => {
      // `key` must stay language-independent since it's matched against One Menu items'
      // `dayParts` arrays - never derive it from `displayName`, which is localized copy.
      const dayPartDisplayName = dayPart.displayName || retrieveDayPartKeyFromId(dayPart.id);

      return {
        displayName: { locale: dayPartDisplayName },
        key: retrieveDayPartKeyFromId(dayPart.id) as DAY_PART_SELECTIONS,
        endTime: dayPart[`${today}Close`] || '',
        startTime: dayPart[`${today}Open`] || '',
      };
    }) || [];

  return remappedDayParts;
};
